import { Button, Label, Modal, Segment } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import type { TresorerieType } from '@/types';
import { isAdresseEmpty, validateMail, validatePhone } from '@/utils';
import ModalPosteComptableConfirm from './ModalPosteComptableConfirm';
import PosteComptableForm from './PosteComptableForm';

type NouveauPosteComptableType = Omit<TresorerieType, 'identifiant'>;

type ModalPosteComptableProps = {
  tresorerie?: NouveauPosteComptableType & { identifiant?: number };
  onClose?: () => void;
  onSave?: (posteComptable: TresorerieType | NouveauPosteComptableType) => void;
};

const ModalPosteComptable = (props: ModalPosteComptableProps) => {
  const { onClose, onSave, tresorerie, ...rest } = props;

  const [form, setForm] = React.useState<TresorerieType>({
    identifiant: tresorerie?.identifiant || null,
    nom: tresorerie?.nom || '',
    code: tresorerie?.code || '',
    mail: tresorerie?.mail || '',
    telephone: tresorerie?.telephone || '',
    identifiantAdresse: tresorerie?.identifiantAdresse || null,
    adresse: tresorerie?.adresse || null,
  });
  const [errors, setErrors] = React.useState<Record<string, string>>();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { nom, code, adresse } = form;

  const validateForm = React.useCallback(() => {
    const required = ['nom', 'code', 'adresse'];

    const errs: Record<string, string> = {};

    _.forEach(required, (field) => {
      if (_.isEmpty(form[field])) {
        errs[field] = `Le ${field} est obligatoire`;
      }

      if (field === 'adresse' && isAdresseEmpty(form[field])) {
        errs[field] = "L'adresse est obligatoire";
      }
    });

    // Validate le numéro de téléphone
    const telErrors = validatePhone(form.telephone);

    if (!_.isEmpty(form.telephone) && !_.isEmpty(telErrors)) {
      errs.telephone = _.head(telErrors)?.message;
    }

    // Validate le mail
    const mailErrors = validateMail(form.mail);
    if (!_.isEmpty(form.mail) && !_.isEmpty(mailErrors)) {
      errs.mail = _.head(mailErrors)?.message;
    }

    if (!_.isEmpty(errs)) {
      return errs;
    }

    return true;
  }, [form]);

  const handleTresoChange = React.useCallback((treso) => {
    setForm((prev) => ({
      ...prev,
      ...treso,
    }));
  }, []);

  const handleSave = React.useCallback(() => {
    const formValidated = validateForm();

    if (formValidated === true) {
      if (onSave) {
        onSave(_.isNil(form.identifiant) ? _.omit(form, 'identifiant') : form);
      }
    } else {
      setErrors(formValidated);
    }
  }, [form, onSave, validateForm]);

  const handleConfirmToggle = React.useCallback(() => {
    const formValidated = validateForm();

    if (formValidated === true) {
      setConfirmOpen((prev) => !prev);
    } else {
      setErrors(formValidated);
    }
  }, [validateForm]);

  return (
    <>
      <Modal closeIcon open style={{ width: 700 }} onClose={onClose} {...rest}>
        <Modal.Header>
          {_.isNil(tresorerie)
            ? "Ajout d'un nouveau poste comptable"
            : "Modification d'un poste comptable"}
        </Modal.Header>
        <Modal.Content scrolling={false}>
          {!_.isEmpty(errors) && (
            <Segment
              basic
              style={{
                marginLeft: '-0.5em',
                padding: 0,
              }}
            >
              <Label color="red" ribbon>
                Des erreurs sont présentes dans le formulaire
              </Label>
            </Segment>
          )}
          <PosteComptableForm
            tresorerie={form}
            errors={errors}
            onChange={handleTresoChange}
            modifiable
            vertical
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            data-testid="modal-poste-comptable-button-close"
            onClick={onClose}
            basic
            size="tiny"
            icon="close"
            content="Annuler"
          />
          <Button
            data-testid="modal-poste-comptable-button-validate"
            positive
            disabled={_.isEmpty(nom) && _.isEmpty(code) && isAdresseEmpty(adresse)}
            onClick={_.isNil(tresorerie?.identifiant) ? handleSave : handleConfirmToggle}
            content="Valider"
            icon="checkmark"
          />
        </Modal.Actions>
      </Modal>
      {confirmOpen && !_.isNil(tresorerie?.identifiant) && (
        <ModalPosteComptableConfirm
          mode="edit"
          onConfirm={handleSave}
          onCancel={handleConfirmToggle}
        />
      )}
    </>
  );
};

export default ModalPosteComptable;
